import { Box, Grid, Slider, useTheme } from '@mui/material'
import { Typography } from '@carto/meridian-ds/components'
import { useAppDispatch, useAppSelector } from 'app/state'
import { setSettingsSnapshot } from 'features/analysis/state/analysisSlice'
import { AnalysisSettings, AnalysisType } from 'features/analysis/types'
import { AppFormInputLabel, AppFormGridColumn } from 'features/apps/ui/AppForm/appsFormStyle'
import { useSourceRegion } from 'features/builder/hooks/builderHooks'
import { getEnrichedDatasetById } from 'features/builder/state/keplerGlSlice'
import { Connection, getConnectionByName } from 'features/connections'
import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import AnalysisBase from '../AnalysisBase'
import AnalysisHeader from '../AnalysisHeader'
import ClustersInput from './Form/ClustersInput'
import { generateClusteringKMeansQuery } from './queries'
import { ClusteringKMeansSettings } from './types'
import { useATLocation } from 'features/connections/hooks/useATLocation'

function ClusteringKMeansForm({
  settings,
  onChange,
  onQueryChange
}: {
  settings: ClusteringKMeansSettings
  onChange: (analysis: AnalysisSettings) => void
  onQueryChange: (query: string | undefined) => void
}) {
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const sourceDatasetId = useAppSelector((state) => state.analysis.sourceDatasetId) || ''
  const sourceDataset = useAppSelector((state) => getEnrichedDatasetById(state, sourceDatasetId as string))
  const connection = useAppSelector((state) =>
    getConnectionByName(state, { connection: sourceDataset.connectionName })
  ) as Connection

  const { data: tableRegion } = useSourceRegion(sourceDataset, connection)
  const { atLocation } = useATLocation({ connectionId: connection?.id, region: tableRegion })

  const [clusters, setClusters] = useState(settings.clusters || 2)

  useEffect(() => {
    if (sourceDataset && atLocation) {
      onQueryChange(
        generateClusteringKMeansQuery({
          mainSource: sourceDataset,
          clusters,
          atLocation
        })
      )
    }
  }, [sourceDataset, clusters, onQueryChange, atLocation])

  useEffect(() => {
    return () => {
      const settings: ClusteringKMeansSettings = {
        mainSource: sourceDatasetId,
        clusters,
        analysisType: AnalysisType.ClusteringKMeans
      }
      dispatch(setSettingsSnapshot(settings))
      onChange(settings)
    }
  }, [sourceDatasetId, clusters, dispatch, onChange])

  const handleClustersSlideChange = (event, newValue) => {
    setClusters(newValue)
  }
  const handleClustersInputChange = (value) => {
    setClusters(value)
  }

  return (
    <>
      <AnalysisHeader analysisType={AnalysisType.ClusteringKMeans} />
      <Grid container spacing={4} mt={3} mb={2}>
        <AppFormGridColumn item sm={6}>
          <AnalysisBase analysisType={AnalysisType.ClusteringKMeans} />
        </AppFormGridColumn>
        <Grid item sm={6}>
          <Box mb={1.5}>
            <Typography variant="subtitle2">
              <FormattedMessage id="analysis.clusteringKMeans.params" />
            </Typography>
          </Box>
          <Grid container direction="row" alignItems="center" pt={0.75}>
            <Grid item xs>
              <AppFormInputLabel id="clustering-k-means-slide-label" variant="outlined">
                <FormattedMessage id={`analysis.clusteringKMeans.clusters`} />
              </AppFormInputLabel>
              <Box ml={0.5}>
                <Slider
                  name="clustering-k-means-slide-label"
                  min={2}
                  max={16}
                  value={typeof clusters === 'number' ? clusters : 2}
                  onChange={handleClustersSlideChange}
                />
              </Box>
            </Grid>
            <Grid item style={{ paddingLeft: theme.spacing(3) }}>
              <ClustersInput clusters={clusters} onChange={handleClustersInputChange} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  )
}

export default ClusteringKMeansForm
